import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ExerciseSession } from "@/components/exercise-session";
import { ListeningControls } from "@/components/listening-controls";
import { PlaySoundsButton } from "@/components/ui/play-sounds-button";
import { ProgressBar } from "@/components/ui/progress-bar";
import { useT } from "@/glossary";
import {
  stopAllSounds,
  usePlayBundledSoundsAction,
} from "@/lib/audio/use-play-sound";
import { usePlaySuccessSoundIfEnabled } from "@/lib/audio/use-success-sound";
import type { TRAIN } from "@/lib/data/words";
import { trainStore } from "@/lib/stores/train";
import { useEffectOnce } from "@/lib/use-effect-once";
import { StressOption } from "./-stress-option";

const AUTOPLAY_DELAY = 400;

export const Route = createFileRoute("/train/exercise")({
  component: TrainExercise,
});

function TrainExercise() {
  return (
    <ExerciseSession id="train">
      <ActiveExercise />
    </ExerciseSession>
  );
}

const ActiveExercise = () => {
  const navigate = useNavigate();
  const [hasStarted, setHasStarted] = useState(false);
  const wordIndex = trainStore.useCurrentWordIndex();
  const view = trainStore.useCurrent();

  useEffect(() => () => stopAllSounds(), []);

  if (view.kind !== "SHOW_CURRENT_WORD") return null;

  const { word, progress } = view.data;
  return (
    <ExerciseWord
      key={wordIndex}
      hasStarted={hasStarted}
      word={word}
      progress={progress}
      onStart={() => setHasStarted(true)}
      onNext={() => {
        const { isLast } = trainStore.confirmResult();
        if (isLast) {
          navigate({ to: "/train/results" });
        }
      }}
    />
  );
};

const ExerciseWord = ({
  word,
  progress,
  hasStarted,
  onStart,
  onNext,
}: {
  word: (typeof TRAIN)[number];
  progress: { current: number; total: number };
  hasStarted: boolean;
  onStart: () => void;
  onNext: () => void;
}) => {
  const t = useT();
  const [selected, setSelected] = useState<number | null>(null);
  const playWord = usePlayBundledSoundsAction([word.audio.baseFileName]);
  const playSuccessSoundIfEnabled = usePlaySuccessSoundIfEnabled();
  const isLastWord = progress.current + 1 === progress.total;

  useEffectOnce({
    effect: () => {
      if (hasStarted) playWord();
    },
  });

  const hasAnswered = selected !== null;
  const isCorrect = hasAnswered && selected === word.stressIndex;

  const handleSelect = (stressIndex: number) => {
    if (!hasStarted || hasAnswered) return;

    setSelected(stressIndex);
    const correct = stressIndex === word.stressIndex;

    trainStore.addAttempt({
      selected: stressIndex,
      correct,
      timestamp: Date.now(),
    });

    if (correct) playSuccessSoundIfEnabled();

    // Auto-replay after wrong answer so user can hear why
    if (!correct) setTimeout(playWord, AUTOPLAY_DELAY);
  };

  return (
    <div className="flex flex-1 flex-col px-4">
      <div className="flex flex-1 flex-col items-center gap-6">
        <ProgressBar
          current={progress.current + 1}
          total={progress.total}
          label={t.common.wordProgress(progress.current + 1, progress.total)}
        />

        <div className="space-y-2 text-center">
          <p className="font-medium text-base text-foreground">
            {t.train.instruction}
          </p>
          <p className="text-muted-foreground text-sm">
            {t.train.clickShapeToAnswer}
          </p>
        </div>

        <div className="flex w-full items-start justify-evenly">
          {Array.from({ length: word.syllableCount }).map((_, i) => (
            <StressOption
              // biome-ignore lint/suspicious/noArrayIndexKey: index is the stress position
              key={i}
              index={i}
              syllableCount={word.syllableCount}
              isCorrectAnswer={i === word.stressIndex}
              isSelected={selected === i}
              isCorrect={isCorrect}
              hasAnswered={hasAnswered}
              disabled={!hasStarted || hasAnswered}
              highlight={hasStarted && !hasAnswered}
              onClick={() => handleSelect(i)}
            />
          ))}
        </div>

        {hasAnswered && (
          <p aria-live="polite" className="font-semibold text-lg">
            {isCorrect ? t.common.goodJob : t.common.notQuite}
          </p>
        )}
      </div>

      <ListeningControls
        hasStarted={hasStarted}
        onStart={() => {
          onStart();
          playWord();
        }}
        hasAnswered={hasAnswered}
        replayButton={
          <PlaySoundsButton
            baseFileNames={[word.audio.baseFileName]}
            playbackDurationMs={word.audio.durationMs}
            label={t.train.replayWord}
            playingLabel={t.train.playing}
            nudge={hasAnswered && !isCorrect}
          />
        }
        onNext={onNext}
        nextLabel={isLastWord ? t.common.next : t.train.nextWord}
      />
    </div>
  );
};
